import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, Navigate, useNavigate, NavLink } from 'react-router-dom';
import { DataRequestBuilder } from "@radixdlt/radix-dapp-toolkit";
import About from './components/About';
import Tokenomics from './components/Tokenomics';
import Roadmap from './components/roadmap';
import SaleSection from './components/SaleSection';
import { rdt } from './radixConfig';
import './App.css';
import { FaInstagram, FaXTwitter, FaTelegram } from 'react-icons/fa6';
import Battle from './components/Battle';
import TraitsAndRarities from './components/TraitsAndRarities';
import { RadixProvider, useRadix } from './RadixContext';
import { AiOutlineHome } from 'react-icons/ai';
import MobileMenu from './components/MobileMenu';
import { Analytics } from "@vercel/analytics/react"
import Search from './components/Search';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/nft-project';
  const isTokenomicsPage = location.pathname === '/tokenomics';
  const navigate = useNavigate();
  const rdt = useRadix();
  const [walletData, setWalletData] = useState(null);
  const [rdtInitialized, setRdtInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const updateWalletData = useCallback(async () => {
    if (!rdt || !rdt.walletApi) return;
    try {
      const data = await rdt.walletApi.getWalletData();
      console.log('App: Wallet data fetched:', data);
      setWalletData(data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    }
  }, [rdt]);

  useEffect(() => {
    const initializeRdt = async () => {
      if (!rdt) return;
      while (!rdt.gatewayApi) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      setRdtInitialized(true);
    };

    initializeRdt();

    if (rdt && rdt.walletApi) {
      rdt.walletApi.setRequestData(DataRequestBuilder.accounts().exactly(1));

      const subscription = rdt.walletApi.walletData$.subscribe({
        next: (data) => {
          console.log('App: Wallet data updated:', data);
          setWalletData(data);
        },
        error: (error) => console.error("Wallet data subscription error:", error),
      });

      console.log('App: Initial wallet data check...');
      updateWalletData();

      return () => subscription.unsubscribe();
    }
  }, [rdt, updateWalletData]);

  const connected = walletData && walletData.accounts && walletData.accounts.length > 0;
  const accountAddress = connected ? walletData.accounts[0].address : '';

  //console.log('App: Rendering with walletData =', walletData);
  console.log('App: connected =', connected, 'accountAddress =', accountAddress);

  const handleMenuToggle = (event) => {
    event.preventDefault(); // Prevent default action
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const path = location.pathname;

    if (isMobile) {
      document.body.className = ''; // Clear previous classes
      if (path === '/') {
        document.body.classList.add('home-background');
      } else if (path === '/sale') {
        document.body.classList.add('sale-background');
      } else if (path === '/tokenomics') {
        document.body.classList.add('tokenomics-background');
      } else if (path === '/about') {
        document.body.classList.add('about-background');
      } else if (path === '/battle') {
        document.body.classList.add('battle-background');
      } else if (path === '/roadmap') {
        document.body.classList.add('roadmap-background');
      } else if (path === '/traits-and-rarities') {
        document.body.classList.add('traits-rarities-background');
      } else if (path === '/search') {
        document.body.classList.add('search-background');
      }
    } else {
      // Remove all background classes from body on desktop
      document.body.className = '';
    }
  }, [location, isMobile]);

  return (
    <div className={`App ${isTokenomicsPage ? 'tokenomics-page' : ''}`}>
      <div className={`content-wrapper ${location.pathname.slice(1)}-background`}>
        {/* Desktop Navigation */}
        <nav className="nav-menu">
          {!isHomePage && <NavLink to="/">Home</NavLink>}
          <NavLink to="/about">About</NavLink>
          <NavLink to="/tokenomics">Tokenomics</NavLink>
          <NavLink to="/roadmap">Roadmap</NavLink>
          <NavLink to="/traits-and-rarities">Traits/Rarity</NavLink>
          <NavLink to="/search">Search</NavLink>
        </nav>

        {/* Mobile Navigation */}
        <MobileMenu />

        <div className="scrollable-content">
          <div className="radix-connect-button-container">
            <radix-connect-button />
          </div>

          {isHomePage && (
            <>
              <img src={`${process.env.PUBLIC_URL}/images/title.PNG`} alt="CAPYCLUB" className="title-overlay" />
              <div className="button-container">
                <button className="main-button battle" onClick={() => navigate('/battle')}>Battle</button>
                <button className="main-button mint" onClick={() => navigate('/sale')}>Mint</button>
              </div>
            </>
          )}

          <div className="social-buttons">
            <a
              href="https://x.com/bottom_dwellers"
              target="_blank"
              rel="noopener noreferrer"
              className="social-button twitter"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://x.com/bottom_dwellers', '_blank', 'noopener,noreferrer');
              }}
            >
              <FaXTwitter />
            </a>
            <a
              className="social-button"
              onClick={(e) => e.preventDefault()}
              style={{ cursor: 'pointer' }}
            >
              <FaTelegram />
              <div className="telegram-tooltip">Telegram Coming Soon!</div>
            </a>
            <Link to="/" className="social-button home">
              <AiOutlineHome />
            </Link>
          </div>

          <Routes>
            <Route path="/" element={null} />
            <Route path="/nft-project" element={<Navigate to="/" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/tokenomics" element={<Tokenomics />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/traits-and-rarities" element={<TraitsAndRarities />} />
            <Route path="/battle" element={<Battle />} />
            <Route path="/sale" element={
              <SaleSection
                connected={connected}
                accountAddress={accountAddress}
                walletData={walletData}
              />
            } />
            <Route path="/search" element={<Search />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <RadixProvider>
        <App />
        <Analytics />
      </RadixProvider>
    </Router>
  );
}
